/* Verbatim port of WS.renderNotesCard (service/review/index.html
 * ~4442-4494) — the §0 "team notes & ideas" card (#wsNotes).
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * Every button/input this renders is owned entirely by this function (no
 * cross-module ownership like search.ts/timeline.ts) — converts fully,
 * including onclick="WS._notesAll=...;WS.renderNotesCard()" -> @click and
 * the trailing querySelectorAll wiring -> @click/@click on the note-save
 * button.
 *
 * F4 item 3 (store decoupling): subscribes to wsChangeStore (state/
 * wsChange.ts) below so a WS data change (refresh(), WS.init() finishing
 * its probe) re-renders this card too, matching what `init()` — but not
 * `repaintAll()` — used to do explicitly (see state/wsChange.ts's own doc
 * comment on why unifying the two is behavior-safe). `WS.renderNotesCard()`
 * (the instance-method wrapper) STAYS — this file's own body still calls it
 * directly, four times, as a targeted self-re-render after a local-only
 * change (resolve/show-more/save-note), which is unrelated to this
 * subscription and untouched by it. The subscription callback references
 * the bare global `WS` rather than importing it — same one-off exception
 * ws/strip.ts's subscription takes, for the same reason (a module-scope
 * callback has no caller to receive `ws` as a parameter from).
 */
import { html, nothing, render } from 'lit';
import { wsChangeStore } from '../state/wsChange.ts';
import { mentionBlur, mentionInput, mentionKeydown, renderMentionBody } from './mentions.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsNote } from '../types/workspace.ts';

export function renderNotesCard(ws: Workspace): void {
  const el = document.getElementById('wsNotes'); if (!el) return;
  if (ws.available !== true) { render(html``, el); return; }
  const open = ws.notes.filter((n) => !n.resolved_at);
  const shown = ws._notesAll ? open : open.slice(0, 5);
  render(html`
    <div class="wsnotescard">
      <h3>Team notes & ideas</h3>
      <div class="sub" style="margin-bottom:8px">shared context: client-call takeaways, ideas to scope, things the next run should treat as known</div>
      ${shown.length ? shown.map((n) => html`
        <div class="wsnote" data-nid="${n.id}">
          <div class="b">${renderMentionBody(n.body)}
            <div class="who">${n.author || ''} · ${(n.created_at||'').slice(0,10)}
              ${n.is_idea ? html`<span class="tag">idea</span>` : nothing}
              ${n.feed_next_run ? html`<span class="tag" title="injected into the next research run as known context">feeds next run</span>` : nothing}</div></div>
          <button class="wsmini" @click=${() => ws.addCustomTask(n.body, n.id)}>→ task</button>
          <button class="wsmini" @click=${async () => {
            try { await ws.api(`/notes/${n.id}/resolve`, { method: 'POST', body: '{}' }); } catch (e) {}
            try { ws.notes = ((await ws.api<{ notes?: WsNote[] }>(`/clients/${ws.clientSlug}/notes`)).notes) || []; } catch (e) {}
            ws.renderNotesCard();
          }}>resolve</button>
        </div>`) : html`<div style="font-size:12px;color:var(--muted);padding:6px 0">no open notes</div>`}
      ${open.length > 5 && !ws._notesAll ? html`<button class="wsmini" style="margin-top:8px" @click=${() => { ws._notesAll = true; ws.renderNotesCard(); }}>show all ${open.length} notes</button>` : nothing}
      ${ws._notesAll && open.length > 5 ? html`<button class="wsmini" style="margin-top:8px" @click=${() => { ws._notesAll = false; ws.renderNotesCard(); }}>show recent only</button>` : nothing}
      <div style="margin-top:10px">
        <textarea id="wsNewNote" rows="2" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:7px;font-family:var(--sans);font-size:12.5px" placeholder="e.g. Cargo app is being retired in Q4 — don't flag as stale · type @ to mention a teammate" @input=${(e: Event) => mentionInput(e, ws)} @keydown=${(e: KeyboardEvent) => mentionKeydown(e)} @blur=${() => mentionBlur()}></textarea>
        <div style="display:flex;gap:10px;align-items:center;margin-top:6px;font-size:11.5px;color:var(--muted)">
          <label><input type="checkbox" id="wsNoteIdea"> idea</label>
          <label><input type="checkbox" id="wsNoteFeed"> feed next run (mark as known, never re-flag cold)</label>
          <button class="wsmini" id="wsNoteSave" style="margin-left:auto" @click=${async () => {
            const body = (document.getElementById('wsNewNote') as HTMLTextAreaElement).value.trim(); if (!body) return;
            // `author` STRIPPED (Phase 0 auth, Task 5) — see state/
            // workspace.ts's migrateOnce() comment; ensureAuthor() no
            // longer needs to be awaited here either (it never prompts).
            try {
              await ws.api(`/clients/${ws.clientSlug}/notes`, { method: 'POST',
                body: JSON.stringify({ body,
                  is_idea: (document.getElementById('wsNoteIdea') as HTMLInputElement).checked,
                  feed_next_run: (document.getElementById('wsNoteFeed') as HTMLInputElement).checked,
                  target_type: 'client' }) });
              ws.toast('Note saved.', 'ok');
              ws.notes = ((await ws.api<{ notes?: WsNote[] }>(`/clients/${ws.clientSlug}/notes`)).notes) || [];
              ws.renderNotesCard();
            } catch (e) { ws.toast('Note failed: ' + (e as Error).message, 'warn'); }
          }}>Save note</button>
        </div>
      </div>
    </div>`, el);
}

// F4 item 3 — see the module doc comment.
wsChangeStore.subscribe(() => renderNotesCard(WS));
